import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Workflow } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { WorkflowCard } from '@/components/workflows/workflow-card'
import { CreateWorkflowDialog } from '@/components/workflows/create-workflow-dialog'
import { useWorkflowStore } from '@/stores/workflow-store'
import { useOrganizationStore } from '@/stores/organization-store'

function WorkflowCardSkeleton() {
  return (
    <Card variant="glass" className="p-6 animate-pulse">
      <div className="flex items-start justify-between mb-4">
        <div className="w-10 h-10 rounded-xl bg-gray-700/50" />
        <div className="w-16 h-6 rounded-full bg-gray-700/50" />
      </div>
      <div className="space-y-2 mb-4">
        <div className="h-5 bg-gray-700/50 rounded w-3/4" />
        <div className="h-4 bg-gray-700/50 rounded w-1/2" />
      </div>
      <div className="h-4 bg-gray-700/50 rounded w-full" />
    </Card>
  )
}

function EmptyState({ onCreateClick }: { onCreateClick: () => void }) {
  const { t } = useTranslation('workflows')

  return (
    <div className="col-span-full flex flex-col items-center justify-center py-16">
      <div className="w-20 h-20 rounded-2xl bg-gray-700/30 flex items-center justify-center mb-6">
        <Workflow className="w-10 h-10 text-gray-500" />
      </div>
      <h3 className="text-xl font-semibold text-gray-200 mb-2">
        {t('empty.title')}
      </h3>
      <p className="text-gray-500 text-center max-w-md mb-6">
        {t('empty.description')}
      </p>
      <Button
        variant="action-indigo"
        className="gap-2"
        onClick={onCreateClick}
      >
        <Plus className="w-5 h-5" />
        {t('createButton')}
      </Button>
    </div>
  )
}

function NoProjectState() {
  const { t } = useTranslation('workflows')

  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="w-20 h-20 rounded-2xl bg-amber-500/20 flex items-center justify-center mb-6">
        <Workflow className="w-10 h-10 text-amber-400" />
      </div>
      <h3 className="text-xl font-semibold text-gray-200 mb-2">
        {t('list.noProjectSelected')}
      </h3>
      <p className="text-gray-500 text-center max-w-md">
        {t('list.noProjectDesc')}
      </p>
    </div>
  )
}

export function WorkflowsPage() {
  const { t } = useTranslation('workflows')
  const navigate = useNavigate()
  const { currentProject } = useOrganizationStore()
  const { workflows, loading, fetchWorkflows, page, totalPages } = useWorkflowStore()
  // **O ATALHO DO PAINEL ABRE O DIÁLOGO, e não só a lista.** O botão "Novo
  // Workflow" das Ações Rápidas apontava para `/workflows/new`, que não é rota
  // nenhuma: casava com `/workflows/:workflowId` e o editor ia buscar um fluxo
  // de id "new". Mandá-lo para a lista consertaria o erro e quebraria a
  // promessa do rótulo, então ele traz `?novo=1` e cai direto na criação.
  const [parametros, setParametros] = useSearchParams()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  // Quem abre é ESTE efeito, e só ele. Semear o `useState` com o parâmetro
  // também funcionava, e por isso era pior: dois mecanismos para um
  // comportamento, e apagar um deles não quebrava teste nenhum.
  useEffect(() => {
    if (parametros.get('novo') !== '1') return
    setCreateDialogOpen(true)
    // O parâmetro sai da barra de endereços depois de cumprir o papel: sem
    // isso, recarregar a página reabriria o diálogo para sempre.
    parametros.delete('novo')
    setParametros(parametros, { replace: true })
  }, [parametros, setParametros])

  useEffect(() => {
    if (currentProject?.id) {
      fetchWorkflows(currentProject.id)
    }
  }, [currentProject?.id, fetchWorkflows])

  const handleWorkflowClick = (workflowId: string) => {
    navigate(`/workflows/${workflowId}`)
  }

  const handlePageChange = (newPage: number) => {
    if (currentProject?.id) {
      fetchWorkflows(currentProject.id, newPage)
    }
  }

  if (!currentProject) {
    return <NoProjectState />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-100">{t('title')}</h1>
        <Button
          variant="action-indigo"
          className="gap-2"
          onClick={() => setCreateDialogOpen(true)}
        >
          <Plus className="w-5 h-5" />
          {t('createButton')}
        </Button>
      </div>

      {/* Workflow Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          Array.from({ length: 6 }).map((_, index) => (
            <WorkflowCardSkeleton key={index} />
          ))
        ) : workflows.length === 0 ? (
          <EmptyState onCreateClick={() => setCreateDialogOpen(true)} />
        ) : (
          workflows.map((workflow) => (
            <WorkflowCard
              key={workflow.id}
              workflow={workflow}
              onClick={() => handleWorkflowClick(workflow.id)}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => handlePageChange(page - 1)}
            className="border-gray-700/50 text-gray-300"
          >
            &laquo;
          </Button>
          <span className="text-sm text-gray-400">
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => handlePageChange(page + 1)}
            className="border-gray-700/50 text-gray-300"
          >
            &raquo;
          </Button>
        </div>
      )}

      {/* Create Dialog */}
      <CreateWorkflowDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
    </div>
  )
}
